import { VoterSearchForm } from 'src/@types/voterSearch';

// components
import { Button, Grid, MenuItem, Stack, Typography, Skeleton, alpha } from '@mui/material';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { useOrg } from 'src/hooks/query/orgHooks';
import { statesSettings } from 'src/assets/data/states';
import { useMemo } from 'react';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useVoterSearchCounties, useVoterSearchPrecincts } from 'src/hooks/query/voterSearch';
import { getDataSourceLabel } from 'src/utils/getDataSourceLabel';

type VoterLookupSearchScreenProps = {
  searchValues?: VoterSearchForm;
  onSearch: (data: VoterSearchForm) => void;
  onClear?: () => void;
  showMore?: boolean;
  setShowMore?: React.Dispatch<React.SetStateAction<boolean>>;
};

function VoterLookupSearchScreen({
  searchValues,
  onSearch,
  onClear,
  showMore = false,
  setShowMore,
}: VoterLookupSearchScreenProps) {
  const { data: org } = useOrg();

  const methods = useForm<VoterSearchForm>({
    defaultValues: searchValues,
    values: searchValues,
  });

  const {
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const handleClose = () => {
    onClear && onClear();
    reset();
  };

  const watchedValues = watch();

  const hasValues = useMemo(() => {
    return Object.entries(watchedValues).some(([key, value]) => {
      if (key === 'state' && value === org?.selected_state) {
        return false;
      }
      return Boolean(value);
    });
  }, [watchedValues, org?.selected_state]);

  const countyQuery = useVoterSearchCounties(watchedValues.state || '', {
    enabled: !!watchedValues.state,
  });

  const precinctQuery = useVoterSearchPrecincts(
    watchedValues.state || '',
    watchedValues.county_name || '',
    {
      enabled: !!watchedValues.state && !!watchedValues.county_name,
    }
  );

  const counties: string[] = countyQuery.data ?? [];
  const precincts: string[] = precinctQuery.data ?? [];

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSearch)}>
      <Stack direction="column" spacing={0.25} alignItems="center" justifyContent="center" mb={2}>
        <Typography variant="h5">Look up Voters</Typography>
        <Typography variant="body2" color="textSecondary">
          Search voters by name, phone number, email, and address.
        </Typography>
      </Stack>
      <Stack spacing={3} alignItems="center" maxHeight="50vh" overflow="auto">
        <Stack spacing={1}>
          <Typography variant="subtitle1">Name</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <RHFTextField
                name="first_name"
                label="First Name"
                error={!!errors.first_name}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <RHFTextField
                name="middle_name"
                label="Middle Name"
                error={!!errors.middle_name}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <RHFTextField
                name="last_name"
                label="Last Name"
                error={!!errors.last_name}
                size="small"
              />
            </Grid>
          </Grid>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle1">Location</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <RHFTextField name="address" label="Address" error={!!errors.address} size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <RHFTextField name="city" label="City" error={!!errors.city} size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <RHFSelect
                name="state"
                label="State"
                error={!!errors.state}
                disabled={org?.selected_state !== 'NA' && org?.selected_state !== 'NS'}
                defaultValue={org?.selected_state || ''}
                children={statesSettings.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <RHFTextField
                name="zip_code"
                label="Zip Code"
                error={!!errors.zip_code}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {countyQuery.isLoading ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <RHFSelect
                  name="county_name"
                  label="County"
                  error={!!errors.county_name}
                  defaultValue=""
                  size="small"
                  sx={{
                    backgroundColor: (theme) =>
                      !watchedValues.state
                        ? alpha(theme.palette.grey[500], 0.24)
                        : theme.palette.common.white,
                    borderRadius: 1,
                  }}
                  disabled={!watchedValues.state}
                  children={[
                    <MenuItem key="none" value="">
                      None
                    </MenuItem>,
                    ...counties.map((county) => (
                      <MenuItem key={county} value={county}>
                        {county}
                      </MenuItem>
                    )),
                  ]}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {precinctQuery.isLoading ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <RHFSelect
                  name="precinct_name"
                  label="Precinct"
                  error={!!errors.precinct_name}
                  defaultValue=""
                  size="small"
                  sx={{
                    backgroundColor: (theme) =>
                      !watchedValues.state || !watchedValues.county_name
                        ? alpha(theme.palette.grey[500], 0.24)
                        : theme.palette.common.white,
                    borderRadius: 1,
                  }}
                  disabled={!watchedValues.state || !watchedValues.county_name}
                  children={[
                    <MenuItem key="none" value="">
                      None
                    </MenuItem>,
                    ...precincts.map((precinct) => (
                      <MenuItem key={precinct} value={precinct}>
                        {precinct}
                      </MenuItem>
                    )),
                  ]}
                />
              )}
            </Grid>
          </Grid>
        </Stack>
        <Stack spacing={3} display={showMore ? 'block' : 'none'}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">Contact</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField
                  name="phone"
                  label="Phone Number"
                  error={!!errors.phone}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField name="email" label="Email" error={!!errors.email} size="small" />
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">Voter IDs</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField
                  name="data_source_id"
                  label={getDataSourceLabel(org)}
                  error={!!errors.data_source_id}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField
                  name="numinar_id"
                  label="Numinar ID"
                  error={!!errors.numinar_id}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField
                  name="state_voter_id"
                  label="State Voter ID"
                  error={!!errors.state_voter_id}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <RHFTextField
                  name="jurisdiction_voter_id"
                  label="County/Jurisdiction Voter ID"
                  error={!!errors.jurisdiction_voter_id}
                  size="small"
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-start" width="100%">
          <Button
            variant="text"
            color="primary"
            onClick={() => setShowMore && setShowMore(!showMore)}
            endIcon={showMore ? <ExpandLess /> : <ExpandMore />}
          >
            {showMore ? 'Fewer' : 'More'} Options
          </Button>
        </Stack>
        <Stack direction="row-reverse" spacing={1} width="100%" alignItems="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!!Object.keys(errors).length || !hasValues}
          >
            Search
          </Button>
          <Button type="button" variant="text" color="primary" onClick={handleClose}>
            Clear
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
}

export default VoterLookupSearchScreen;
